import React, { useState, useCallback } from 'react';
import { SalesAnalyticsFilters } from '../../types/analytics';
import { analyticsService } from '../../services/analyticsService';
import SalesMetricsDashboard from './SalesMetricsDashboard';
import VehiclePerformanceAnalytics from './VehiclePerformanceAnalytics';
import RealTimeSalesUpdates from './RealTimeSalesUpdates';

const SalesAnalytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'vehicles' | 'realtime'>('overview');
  const [filters, setFilters] = useState<SalesAnalyticsFilters>(() => {
    const dates = analyticsService.calculatePeriodDates('monthly');
    return {
      startDate: dates.current.start,
      endDate: dates.current.end,
      period: 'monthly'
    };
  });

  const [topVehicleTypes, setTopVehicleTypes] = useState([
    {
      vehicleType: 'Tesla Model 3',
      count: 45,
      revenue: 15000,
      averageDuration: 2.5
    },
    {
      vehicleType: 'BMW X5',
      count: 30,
      revenue: 12000,
      averageDuration: 3.2
    },
    {
      vehicleType: 'Mercedes C-Class',
      count: 25,
      revenue: 10000,
      averageDuration: 2.8
    }
  ]);

  const handleFiltersChange = useCallback((newFilters: SalesAnalyticsFilters) => {
    setFilters(newFilters);
  }, []);

  const handleRealTimeUpdate = useCallback((updateData: any) => {
    // Handle real-time updates to refresh data if needed
    console.log('Real-time update received:', updateData);
    
    // You could trigger a refresh of the dashboard data here
    // or update specific metrics based on the update type
  }, []);

  const tabs = [
    { id: 'overview', label: 'Sales Overview', icon: '📊' },
    { id: 'vehicles', label: 'Vehicle Performance', icon: '🚗' },
    { id: 'realtime', label: 'Real-time Updates', icon: '⚡' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sales Analytics</h1>
            <p className="text-gray-600">
              Comprehensive sales performance and vehicle analytics dashboard
            </p>
          </div>
          
          {/* Date Range Display */}
          <div className="text-right">
            <div className="text-sm text-gray-500">Current Period</div>
            <div className="font-medium text-gray-900">
              {filters.startDate.toLocaleDateString()} - {filters.endDate.toLocaleDateString()}
            </div>
            <div className="text-sm text-gray-500 capitalize">{filters.period} View</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-screen">
        {activeTab === 'overview' && (
          <SalesMetricsDashboard
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        )}

        {activeTab === 'vehicles' && (
          <VehiclePerformanceAnalytics
            startDate={filters.startDate}
            endDate={filters.endDate}
            topVehicleTypes={topVehicleTypes}
          />
        )}

        {activeTab === 'realtime' && (
          <RealTimeSalesUpdates
            onDataUpdate={handleRealTimeUpdate}
          />
        )}
      </div>

      {/* Quick Stats Footer */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {analyticsService.formatCurrency(
                topVehicleTypes.reduce((sum, type) => sum + type.revenue, 0)
              )}
            </div>
            <div className="text-sm text-gray-600">Total Vehicle Revenue</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-green-600">
              {topVehicleTypes.reduce((sum, type) => sum + type.count, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Rentals</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-purple-600">
              {topVehicleTypes.length}
            </div>
            <div className="text-sm text-gray-600">Vehicle Types</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-orange-600">
              {(topVehicleTypes.reduce((sum, type) => sum + type.averageDuration, 0) / topVehicleTypes.length).toFixed(1)}
            </div>
            <div className="text-sm text-gray-600">Avg Rental Days</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesAnalytics;